<template>
  <div class="cs-media-preview">
    <NSpin v-if="isLoading" class="w-full" :description="loadingMessage" />
    <img
      v-show="!isLoading && isImage && url"
      alt="Preview"
      class="max-w-full h-auto max-h-[400px]"
      :src="url"
      @error="onLoadError"
      @load="onLoad"
    />
    <video
      v-show="!isLoading && isVideo && url"
      class="max-w-full h-auto max-h-[400px]"
      controls
      :src="url"
      @error="onLoadError"
      @loadeddata="onLoad"
    >
      {{ t('canvasUI.contentSelector.mediaPreview.noVideoBrowser') }}
    </video>
    <div v-show="!isLoading && !url" class="pa-5 text-center rounded-2" :style="{ background: themeVars.primary5 }">
      {{ t('canvasUI.contentSelector.mediaPreview.previewNotAvailable') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSpin } from 'naive-ui'

import { getMediaType } from '@content-selector/util/media-type.util'
import useThemeVars from '@lib/composables/useThemeVars'

const emit = defineEmits(['load'])

type Props = {
  url?: string
  type?: 'image' | 'video'
  loadingMessage?: string
}

const props = defineProps<Props>()
const { t } = useI18n()
const themeVars = useThemeVars()
const mediaType = ref<'image' | 'video' | null>(null)

const isLoading = ref(false)
let loadingTimeout: number | null = null

const onLoad = () => {
  clearLoadingTimeout()
  isLoading.value = false
  emit('load')
}

const onLoadError = () => {
  clearLoadingTimeout()
  isLoading.value = false
}

const clearLoadingTimeout = () => {
  if (loadingTimeout) {
    clearTimeout(loadingTimeout)
    loadingTimeout = null
  }
}

watchEffect(async () => {
  if (props.url) {
    isLoading.value = true
    clearLoadingTimeout()
    loadingTimeout = setTimeout(() => {
      isLoading.value = false
    }, 60 * 1000) as unknown as number
  } else {
    isLoading.value = false
    clearLoadingTimeout()
  }

  if (props.type) {
    mediaType.value = props.type
  } else if (props.url) {
    mediaType.value = await getMediaType(props.url)
  } else {
    mediaType.value = null
  }
})

const isImage = computed(() => mediaType.value === 'image')
const isVideo = computed(() => mediaType.value === 'video')
</script>
